import type { Car, Driver } from '@multirx/shared';
import type { DriverSeasonProfile } from '@multirx/shared/game/driverPalmares.js';
import type { GlobalActiveSeasonView } from '@multirx/shared/types/championship.js';
import { driverAvatarImageClass } from '@multirx/shared/game/driverAvatarCrop.js';
import { fetchActiveGlobalSeason, fetchDriverSeasonProfile } from '../championship/api.js';
import {
  clearCar3dPreviewSnapshotCache,
  mountCar3dPreview,
  type Car3dPreviewHandle,
} from '../cars/car3dPreview.js';
import { openDriverDetail } from '../drivers/openDriverDetail.js';
import {
  buildFeaturedPilotCardModel,
  featuredPilotCardInnerHtml,
} from './featuredPilotCardModel.js';
import { pickNextFeaturedPilotId } from './featuredPilotPick.js';
import { playFeaturedPilotTransition } from './featuredPilotTransition.js';

export const FEATURED_PILOT_ROTATE_MS = 10_000;

export type FeaturedPilotCardHandle = {
  dispose(): void;
};

type MountFeaturedPilotCardOptions = {
  drivers: readonly Driver[];
  cars: readonly Car[];
  rotateMs?: number;
  globalView?: GlobalActiveSeasonView | null;
};

function seasonIdForDriver(
  globalView: GlobalActiveSeasonView | null,
  division: number | undefined
): number | null {
  if (!globalView || division == null) return null;
  if (division !== 1 && division !== 2 && division !== 3 && division !== 4) return null;
  return globalView.divisions[division]?.season.id ?? null;
}

function isDashboardViewActive(): boolean {
  return Boolean(document.getElementById('view-dashboard')?.classList.contains('is-active'));
}

export function mountFeaturedPilotCard(
  host: HTMLElement,
  options: MountFeaturedPilotCardOptions
): FeaturedPilotCardHandle {
  const rotateMs = options.rotateMs ?? FEATURED_PILOT_ROTATE_MS;
  const carsById = new Map(
    options.cars.filter((c): c is Car & { id: number } => c.id != null).map((c) => [c.id, c])
  );
  const driversById = new Map(
    options.drivers.filter((d): d is Driver & { id: number } => d.id != null).map((d) => [d.id, d])
  );
  const driverIds = [...driversById.keys()];

  clearCar3dPreviewSnapshotCache();

  host.className = 'card-glow-wrap card-glow-wrap--featured card-glow-wrap--featured-pilot';
  host.innerHTML = `
    <a href="#garage/pilotes" class="card-glow card-glow--preview featured-pilot-card" data-featured-pilot-link>
      <p class="featured-pilot-loading">Chargement des pilotes…</p>
    </a>`;

  const link = host.querySelector<HTMLAnchorElement>('[data-featured-pilot-link]');
  if (!link || driverIds.length === 0) {
    if (link) {
      link.innerHTML = `<p class="featured-pilot-empty">Aucun pilote à présenter pour l’instant.</p>`;
    }
    return { dispose() {} };
  }

  let disposed = false;
  let lastId: number | null = null;
  let generation = 0;
  let rotating = false;
  let preview: Car3dPreviewHandle | null = null;
  let timer: ReturnType<typeof setInterval> | null = null;
  let globalView: GlobalActiveSeasonView | null = options.globalView ?? null;
  let globalViewLoaded = options.globalView !== undefined;

  const disposePreview = (): void => {
    preview?.dispose();
    preview = null;
  };

  const paint = async (driverId: number): Promise<void> => {
    const gen = ++generation;
    const driver = driversById.get(driverId);
    if (!driver) return;

    if (!globalViewLoaded) {
      try {
        globalView = await fetchActiveGlobalSeason();
      } catch {
        globalView = null;
      }
      globalViewLoaded = true;
      if (disposed || gen !== generation) return;
    }

    let profile: DriverSeasonProfile | null = null;
    const seasonId = seasonIdForDriver(globalView, driver.division);
    if (seasonId != null) {
      try {
        profile = await fetchDriverSeasonProfile(seasonId, driverId);
      } catch {
        profile = null;
      }
    }
    if (disposed || gen !== generation) return;

    const carId = driver.defaultCarId ?? null;
    const car = carId != null ? (carsById.get(carId) ?? null) : null;
    const model = buildFeaturedPilotCardModel(driver, car, profile);

    disposePreview();
    link.innerHTML = featuredPilotCardInnerHtml(model);
    link.dataset.driverId = String(driverId);

    const avatarImg = link.querySelector<HTMLImageElement>('.featured-pilot-avatar-img');
    if (avatarImg && model.driverImagePath) {
      avatarImg.className = driverAvatarImageClass(
        'featured-pilot-avatar-img',
        model.division,
        model.driverImagePath
      );
    }

    const car3dEl = link.querySelector<HTMLElement>('[data-featured-pilot-car-3d]');
    if (car3dEl && model.car?.modelUrl) {
      preview = mountCar3dPreview(car3dEl, model.car.modelUrl, undefined, { snapshot: true });
      car3dEl.classList.add('is-ready');
    }

    lastId = driverId;
  };

  const rotate = (): void => {
    if (disposed || rotating || document.hidden || !isDashboardViewActive()) return;
    const nextId = pickNextFeaturedPilotId(driverIds, lastId);
    if (nextId == null) return;
    // Premier affichage : sans transition. Ensuite : logo + reflet, puis swap.
    if (lastId == null) {
      void paint(nextId);
      return;
    }
    rotating = true;
    // Overlay sur le wrap (pas le lien) : paint() remplace le innerHTML du lien sans tuer l’effet.
    void playFeaturedPilotTransition(host, () => paint(nextId)).finally(() => {
      rotating = false;
    });
  };

  const onClick = (e: MouseEvent): void => {
    e.preventDefault();
    const id = Number(link.dataset.driverId);
    if (Number.isFinite(id) && id > 0) openDriverDetail(id);
  };

  const onVisibility = (): void => {
    if (!document.hidden && isDashboardViewActive() && lastId == null) {
      rotate();
    }
  };

  const onHash = (): void => {
    if (isDashboardViewActive() && lastId == null) rotate();
  };

  link.addEventListener('click', onClick);
  document.addEventListener('visibilitychange', onVisibility);
  window.addEventListener('hashchange', onHash);

  const firstId = pickNextFeaturedPilotId(driverIds, null);
  if (firstId != null) void paint(firstId);

  timer = setInterval(rotate, rotateMs);

  return {
    dispose(): void {
      disposed = true;
      generation += 1;
      if (timer != null) clearInterval(timer);
      timer = null;
      link.removeEventListener('click', onClick);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('hashchange', onHash);
      disposePreview();
    },
  };
}
